/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  productListInterface,
  productsPayload,
  productGroupingList,
  modelRelacaoProdutoBeneficio,
  responsePaymentByProduct,
} from 'interfaces/Product';
import {
  createRelationshipProductBenefitPayload,
  responseRelationshipProductBenefit,
  updateRelationshipProductBenefitPayload,
} from 'interfaces/RelationshipProductBenefit';
import { errorRedux } from 'interfaces/redux';
import { paramsQueryString } from 'utils/objects';
import {
  createProduct,
  createRelationshipProductBenefit,
  deleteProduct,
  deleteRelationshipProductBenefit,
  getDescriptionProduct,
  getPaymentByProduct,
  getProductByBenefitsList,
  getProductGroupList,
  getProductList,
  getProductsForCustomer,
  updateProduct,
  updateRelationshipProductBenefit,
} from './service';

// Action: Listar produtos
export const productList = createAsyncThunk<productListInterface, paramsQueryString, { rejectValue: errorRedux }>(
  'product/list',
  async (payload, { rejectWithValue }) => {
    try {
      const data = await getProductList(payload);
      return data;
    } catch (error) {
      const err = error as AxiosError<errorRedux>;
      return rejectWithValue(err.response?.data as errorRedux);
    }
  },
);

// Action: Buscar produto por ID
export const getProduct = createAsyncThunk<productListInterface, productsPayload, { rejectValue: errorRedux }>(
  'product/',
  async (payload, { rejectWithValue }) => {
    try {
      const data = await getDescriptionProduct(payload);
      return data;
    } catch (error) {
      const err = error as AxiosError<errorRedux>;
      return rejectWithValue(err.response?.data as errorRedux);
    }
  },
);

// Action: Criar produto
export const productCreate = createAsyncThunk<productListInterface, any, { rejectValue: errorRedux }>(
  'product/create',
  async (payload, { rejectWithValue }) => {
    try {
      const data = await createProduct(payload);
      return data;
    } catch (error) {
      const err = error as AxiosError<errorRedux>;
      return rejectWithValue(err.response?.data as errorRedux);
    }
  },
);

// Action: Atualizar produto
export const productUpdate = createAsyncThunk<
  productListInterface,
  { id: number; data: any },
  { rejectValue: errorRedux }
>('product/update', async (payload, { rejectWithValue }) => {
  try {
    const data = await updateProduct(payload);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Listar agrupamentos
export const getProductGroupingList = createAsyncThunk<
  productGroupingList,
  undefined,
  { rejectValue: errorRedux }
>('product/groups', async (_, { rejectWithValue }) => {
  try {
    const data: any = await getProductGroupList();
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Buscar produtos para cliente
export const productsForCustomer = createAsyncThunk<
  productListInterface,
  { customer: string },
  { rejectValue: errorRedux }
>('product/forCustomer', async (payload, { rejectWithValue }) => {
  try {
    const data = await getProductsForCustomer(payload);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Buscar benefícios por produto
export const productByBenefits = createAsyncThunk<
  modelRelacaoProdutoBeneficio,
  number | undefined,
  { rejectValue: errorRedux }
>('product/byBenefits', async (productId, { rejectWithValue }) => {
  try {
    const data = await getProductByBenefitsList(productId);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Buscar formas de pagamento por produto
export const paymentByProduct = createAsyncThunk<
  responsePaymentByProduct,
  productsPayload,
  { rejectValue: errorRedux }
>('product/payment', async (payload, { rejectWithValue }) => {
  try {
    const data = await getPaymentByProduct(payload);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Criar relacionamento produto-benefício
export const createRelationshipProductBenefitAction = createAsyncThunk<
  responseRelationshipProductBenefit,
  createRelationshipProductBenefitPayload,
  { rejectValue: errorRedux }
>('product/relationshipBenefit/create', async (payload, { rejectWithValue }) => {
  try {
    const data = await createRelationshipProductBenefit(payload);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Atualizar relacionamento produto-benefício
export const updateRelationshipProductBenefitAction = createAsyncThunk<
  responseRelationshipProductBenefit,
  updateRelationshipProductBenefitPayload,
  { rejectValue: errorRedux }
>('product/relationshipBenefit/update', async (payload, { rejectWithValue }) => {
  try {
    const data = await updateRelationshipProductBenefit(payload);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Remover relacionamento produto-benefício
export const deleteRelationshipProductBenefitAction = createAsyncThunk<
  void,
  number,
  { rejectValue: errorRedux }
>('product/relationshipBenefit/delete', async (id, { rejectWithValue }) => {
  try {
    await deleteRelationshipProductBenefit(id);
    return undefined;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Deletar produto
export const productDelete = createAsyncThunk<void, number, { rejectValue: errorRedux }>(
  'product/delete',
  async (id, { rejectWithValue }) => {
    try {
      await deleteProduct(id);
      return undefined;
    } catch (error) {
      const err = error as AxiosError<errorRedux>;
      return rejectWithValue(err.response?.data as errorRedux);
    }
  },
);

// Reducer
interface IProducts extends errorRedux {
  productsList: productListInterface;
  productDescription: productListInterface;
  productGroups: productGroupingList;
  productByBenefits: modelRelacaoProdutoBeneficio;
  productsForCustomer: productListInterface;
  paymentByProduct: responsePaymentByProduct;
}

const initialState: IProducts = {
  status: 'idle',
  message: '',
  productsList: {
    status: '',
    message: '',
    document: [],
    rowsAffected: [],
  },
  productDescription: {
    status: '',
    message: '',
    document: [],
    rowsAffected: [],
  },
  productGroups: {
    status: '',
    message: '',
    document: [],
    rowsAffected: [],
  },
  productByBenefits: {
    status: '',
    message: '',
    document: [],
    rowsAffected: [],
  },
  productsForCustomer: {
    status: '',
    message: '',
    document: [],
    rowsAffected: [],
  },
  paymentByProduct: {
    status: '',
    message: '',
    document: [],
    rowsAffected: [],
    data: {
      codigoProduto: -1,
      bank: [],
      nome: '',
    },
  },
};

export const ProductStatusReducer = createSlice({
  name: 'ProductStatus',
  initialState,
  reducers: {
    clearProductStatus: (state) => {
      state.status = 'idle';
      state.message = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(productList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(productList.fulfilled, (state, action) => {
        state.status = 'completed';
        state.productsList = action.payload;
        state.message = action.payload.message;
      })
      .addCase(productList.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(getProduct.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getProduct.fulfilled, (state, action) => {
        state.status = 'completed';
        state.productDescription = action.payload;
        state.message = action.payload.message;
      })
      .addCase(getProduct.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(productCreate.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(productCreate.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
      })
      .addCase(productCreate.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(productUpdate.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(productUpdate.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload?.message || '';
      })
      .addCase(productUpdate.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(getProductGroupingList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getProductGroupingList.fulfilled, (state, action) => {
        state.status = 'completed';
        state.productGroups = action.payload;
        state.message = action.payload.message;
      })
      .addCase(getProductGroupingList.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(productsForCustomer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(productsForCustomer.fulfilled, (state, action) => {
        state.status = 'completed';
        state.productsForCustomer = action.payload;
        state.message = action.payload.message;
      })
      .addCase(productsForCustomer.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(productByBenefits.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(productByBenefits.fulfilled, (state, action) => {
        state.status = 'completed';
        state.productByBenefits = action.payload;
        state.message = action.payload.message;
      })
      .addCase(productByBenefits.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(paymentByProduct.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(paymentByProduct.fulfilled, (state, action) => {
        state.status = 'completed';
        state.paymentByProduct = action.payload;
        state.message = action.payload.message;
      })
      .addCase(paymentByProduct.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(createRelationshipProductBenefitAction.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createRelationshipProductBenefitAction.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
      })
      .addCase(createRelationshipProductBenefitAction.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(updateRelationshipProductBenefitAction.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateRelationshipProductBenefitAction.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
      })
      .addCase(updateRelationshipProductBenefitAction.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(deleteRelationshipProductBenefitAction.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteRelationshipProductBenefitAction.fulfilled, (state) => {
        state.status = 'completed';
      })
      .addCase(deleteRelationshipProductBenefitAction.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(productDelete.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(productDelete.fulfilled, (state) => {
        state.status = 'completed';
        state.message = 'Produto deletado com sucesso';
      })
      .addCase(productDelete.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      });
  },
});

export const { clearProductStatus } = ProductStatusReducer.actions;
export default ProductStatusReducer.reducer;
